import { useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';

import { useClientes, useContabilidades } from '../../shared/api/queries.ts';
import type { Contabilidade } from '../../shared/api/tipos.ts';
import { normalizarBusca } from '../../shared/format.ts';
import { DialogoContabilidade } from './DialogoContabilidade.tsx';

export function Contabilidades() {
  const { data, isPending, isError, error } = useContabilidades();
  // Carregado aqui para a tabela poder contar os clientes de cada uma sem
  // uma consulta por linha.
  const clientes = useClientes();

  const [busca, setBusca] = useState('');
  const [emEdicao, setEmEdicao] = useState<Contabilidade | undefined>(undefined);
  const [dialogoAberto, setDialogoAberto] = useState(false);

  const contabilidades = data?.itens;

  const visiveis = useMemo(() => {
    if (contabilidades === undefined) return [];

    const termo = normalizarBusca(busca);
    if (termo === '') return contabilidades;

    return contabilidades.filter((item) =>
      normalizarBusca(`${item.razaoSocial} ${item.cnpj}`).includes(termo),
    );
  }, [contabilidades, busca]);

  const clientesPorContabilidade = useMemo(() => {
    const contagem = new Map<string, number>();
    for (const cliente of clientes.data?.itens ?? []) {
      if (cliente.contabilidadeId === null) continue;
      contagem.set(cliente.contabilidadeId, (contagem.get(cliente.contabilidadeId) ?? 0) + 1);
    }
    return contagem;
  }, [clientes.data]);

  function abrirNova() {
    setEmEdicao(undefined);
    setDialogoAberto(true);
  }

  return (
    <Stack spacing={2}>
      <Card>
        <Toolbar sx={{ gap: 2, flexWrap: 'wrap', py: 1.5 }}>
          <Box sx={{ flexGrow: 1, minWidth: 180 }}>
            <Typography variant="h2">Contabilidades</Typography>
            <Typography variant="body2" color="text.secondary">
              Clique em uma contabilidade para editar e vincular clientes.
            </Typography>
          </Box>

          <TextField
            size="small"
            placeholder="Buscar por razão social ou CNPJ"
            value={busca}
            onChange={(evento) => setBusca(evento.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 300 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />

          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNova}>
            Nova contabilidade
          </Button>
        </Toolbar>

        <CardContent sx={{ pt: 0 }}>
          {isError ? (
            <Alert severity="error">
              Não foi possível carregar as contabilidades. {mensagemDoErro(error)}
            </Alert>
          ) : isPending ? (
            <Stack spacing={1} sx={{ py: 1 }}>
              {Array.from({ length: 4 }, (_, indice) => (
                <Skeleton key={indice} variant="rounded" height={40} />
              ))}
            </Stack>
          ) : visiveis.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
              {contabilidades !== undefined && contabilidades.length === 0
                ? 'Nenhuma contabilidade cadastrada ainda. Use "Nova contabilidade" para começar.'
                : `Nenhuma contabilidade encontrada para "${busca}".`}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Razão social</TableCell>
                    <TableCell>CNPJ</TableCell>
                    <TableCell align="right">Clientes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visiveis.map((contabilidade) => (
                    <Linha
                      key={contabilidade.id}
                      contabilidade={contabilidade}
                      clientes={clientesPorContabilidade.get(contabilidade.id) ?? 0}
                      aoAbrir={() => {
                        setEmEdicao(contabilidade);
                        setDialogoAberto(true);
                      }}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {!isPending && !isError && (
        <Typography variant="caption" color="text.secondary">
          {visiveis.length} de {contabilidades?.length ?? 0}{' '}
          {(contabilidades?.length ?? 0) === 1 ? 'contabilidade' : 'contabilidades'}
        </Typography>
      )}

      <DialogoContabilidade
        aberto={dialogoAberto}
        contabilidade={emEdicao}
        aoFechar={() => setDialogoAberto(false)}
      />
    </Stack>
  );
}

function Linha({
  contabilidade,
  clientes,
  aoAbrir,
}: {
  contabilidade: Contabilidade;
  clientes: number;
  aoAbrir: () => void;
}) {
  return (
    <TableRow
      hover
      onClick={aoAbrir}
      // A linha é o controle, então precisa ser acionável pelo teclado como um
      // botão seria — clique do mouse sozinho deixaria a tela inoperável para
      // quem navega por Tab.
      tabIndex={0}
      role="button"
      aria-label={`Editar ${contabilidade.razaoSocial}`}
      onKeyDown={(evento) => {
        if (evento.key === 'Enter' || evento.key === ' ') {
          evento.preventDefault();
          aoAbrir();
        }
      }}
      sx={{
        cursor: 'pointer',
        opacity: contabilidade.ativa ? 1 : 0.6,
        '&:focus-visible': { outline: 2, outlineColor: 'primary.main', outlineOffset: -2 },
      }}
    >
      <TableCell>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <span>{contabilidade.razaoSocial}</span>
          {/* Só a inativa ganha etiqueta: marcar o que é normal vira ruído. */}
          {!contabilidade.ativa && <Chip size="small" label="Inativa" variant="outlined" />}
        </Stack>
      </TableCell>

      {/* Tabular para os CNPJs alinharem dígito a dígito na coluna. */}
      <TableCell sx={{ fontVariantNumeric: 'tabular-nums', width: '1%', whiteSpace: 'nowrap' }}>
        {contabilidade.cnpjFormatado}
      </TableCell>

      <TableCell align="right" sx={{ width: '1%', whiteSpace: 'nowrap' }}>
        <Typography variant="body2" color="text.secondary">
          {clientes}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

function mensagemDoErro(erro: unknown): string {
  return erro instanceof Error ? erro.message : '';
}
